import React from 'react';
import styled from 'styled-components';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText } from './';

const ChartContainer = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme?.shadows?.sm};
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const StatBadge = styled.div`
  background-color: ${props => props.positive ? props.theme.colors.success + '20' : props.theme.colors.danger + '20'};
  color: ${props => props.positive ? props.theme.colors.success : props.theme.colors.danger};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
`;

export const SubmissionsChart = ({ data, loading }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <ChartContainer theme={theme}>
        <ThemedText style={{ textAlign: 'center', padding: theme.spacing.xl }}>
          Loading analytics...
        </ThemedText>
      </ChartContainer>
    );
  }

  if (!data || !data.monthlyData || data.monthlyData.length === 0) {
    return (
      <ChartContainer theme={theme}>
        <ThemedText style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.muted }}>
          No analytics data available
        </ThemedText>
      </ChartContainer>
    );
  }

  const percentageChange = data.percentageChange || 0;
  const isPositive = percentageChange >= 0;

  // Format data for Recharts
  const chartData = data.monthlyData.map(month => ({
    name: month.monthName,
    submissions: month.submissionCount,
    fullMonth: `${month.monthName} ${month.year}`
  }));

  return (
    <ChartContainer theme={theme}>
      <ChartHeader theme={theme}>
        <div>
          <ThemedText type="subtitle">Monthly Submissions</ThemedText>
          <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.xs }}>
            {data.submissionsThisMonth} submissions this month
          </ThemedText>
        </div>
        <StatBadge theme={theme} positive={isPositive}>
          {isPositive ? <TrendingUp size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <TrendingDown size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}{Math.abs(percentageChange).toFixed(1)}%
        </StatBadge>
      </ChartHeader>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
          <XAxis
            dataKey="name"
            stroke={theme.colors.muted}
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke={theme.colors.muted}
            style={{ fontSize: '12px' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: theme.colors.backgroundSecondary,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.text
            }}
            labelStyle={{ color: theme.colors.text }}
          />
          <Legend
            wrapperStyle={{ fontSize: '12px', color: theme.colors.text }}
          />
          <Line
            type="monotone"
            dataKey="submissions"
            stroke={theme.colors.tint}
            strokeWidth={2}
            dot={{ fill: theme.colors.tint, r: 4 }}
            activeDot={{ r: 6 }}
            name="Submissions"
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};
